/**
 * dp-beispiele.js — „Beispielkalender zum Durchblättern“ (sections/dp-beispiele.liquid).
 *
 * Ein Blatt zeigt je Design drei fertige Seiten (Titel, Januar, Juni), gerendert aus dem echten
 * Designer (Bilder dp-beispiel-<design>-<cover|jan|jun>.jpg). Das Liquid liefert die Daten als JSON
 * in <script type="application/json" data-dp-beispiele> und ein leeres Grundgerüst; dieses Modul baut
 * das Karussell (Seiten, Pfeile, Punkte, Design-Chips, Bildunterschrift, CTA) und bedient es.
 *
 * Bedienung: Pfeile, Punkte, Tastatur (←/→, Pos1/Ende), Wischen; Auto-Blättern alle 3,5 s, pausiert
 * bei Hover/Fokus, außerhalb des Sichtfelds (IntersectionObserver) und bei prefers-reduced-motion.
 *
 * Kein Designer-Code wird geladen. renderBeispiele() ist DOM-frei (String-Markup) und getestet in
 * tests/designer/dp-beispiele.test.mjs. Systemgrenze: alle Werte aus dem JSON werden geprüft und
 * escaped; unbekannte Designs/Seiten werden verworfen.
 */

const AUTOPLAY_MS = 3500;
const SWIPE_MIN_PX = 40;
const ID_RE = /^[a-z][a-z0-9-]{0,31}$/;
const PAGE_KEYS = /** @type {const} */ (['cover', 'jan', 'jun']);
const PAGE_LABELS = Object.freeze({ cover: 'Titelseite', jan: 'Januar', jun: 'Juni' });

/**
 * @typedef {object} BeispielDesign
 * @property {string} id        Design-ID (dp-designer-themes-data.js), z. B. 'editorial'
 * @property {string} label     Anzeigename, z. B. 'Editorial'
 * @property {string} format    Formatname, z. B. 'DIN A4 hoch'
 * @property {string} href      Designer-Link mit ?theme=
 * @property {number} width     Bildbreite (1×) in Pixel
 * @property {number} height    Bildhöhe (1×) in Pixel
 * @property {Array<{key:'cover'|'jan'|'jun', src:string, srcset:string, alt:string}>} pages
 */

/** @param {unknown} value @returns {string} */
export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

/**
 * Rohdaten (aus dem Liquid-JSON) prüfen: nur wohlgeformte Designs mit mindestens einer bekannten Seite.
 * @param {unknown} raw @returns {BeispielDesign[]}
 */
export function normalizeDesigns(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const id = String(item.id ?? '').trim().toLowerCase();
    if (!ID_RE.test(id) || seen.has(id)) continue;
    const pages = [];
    for (const key of PAGE_KEYS) {
      const page = Array.isArray(item.pages) ? item.pages.find((p) => p && p.key === key) : null;
      const src = String(page?.src ?? '').trim();
      if (!src || /^(javascript|data):/i.test(src)) continue;
      pages.push({ key, src, srcset: String(page?.srcset ?? '').trim(), alt: String(page?.alt ?? '').trim() || `Beispielseite ${PAGE_LABELS[key]} im Design ${String(item.label ?? id)}` });
    }
    if (!pages.length) continue;
    const width = Number(item.width) > 0 ? Math.round(Number(item.width)) : 620;
    const height = Number(item.height) > 0 ? Math.round(Number(item.height)) : 877;
    const href = String(item.href ?? '').trim();
    seen.add(id);
    out.push({ id, label: String(item.label ?? '').trim() || id, format: String(item.format ?? '').trim(), href: href.startsWith('/') ? href : '', width, height, pages });
  }
  return out;
}

/**
 * Bildunterschrift „Design Editorial · DIN A4 hoch · Januar“.
 * @param {BeispielDesign} design @param {'cover'|'jan'|'jun'} pageKey
 */
export function captionFor(design, pageKey) {
  return [`Design ${design.label}`, design.format, PAGE_LABELS[pageKey] ?? ''].filter(Boolean).join(' · ');
}

/**
 * Markup des Karussells (ohne äußere Section-Hülle): Buch (Bühne + Punkte + Bildunterschrift), Chips, CTA.
 * Erste Seite des aktiven Designs ist sichtbar, alle anderen Seiten sind `hidden`.
 * @param {{ designs: BeispielDesign[], activeId?: string|null, uid?: string, ctaLabel?: string }} view
 * @returns {string}
 */
export function renderBeispiele(view) {
  const designs = view.designs ?? [];
  if (!designs.length) return '';
  const uid = escapeHtml(view.uid || 'dp-beispiele');
  const active = designs.find((d) => d.id === view.activeId) ?? designs[0];
  const ctaLabel = String(view.ctaLabel ?? '').trim() || 'Dieses Design im Designer öffnen';
  // Punkte = Tabs, Seiten = Tabpanels (APG „Carousel with tabs“): Tab steuert Panel, Panel ist nach dem Tab benannt.
  const slides = active.pages.map((page, i) => `
      <div class="dp-beispiele__slide" id="${uid}-panel-${i}" data-page="${page.key}" role="tabpanel" aria-roledescription="Seite" aria-labelledby="${uid}-tab-${i}"${i === 0 ? '' : ' hidden'}>
        <img class="dp-beispiele__img" src="${escapeHtml(page.src)}"${page.srcset ? ` srcset="${escapeHtml(page.srcset)}"` : ''} sizes="(min-width: 990px) 520px, 88vw" width="${active.width}" height="${active.height}" loading="${i === 0 ? 'eager' : 'lazy'}" decoding="async" alt="${escapeHtml(page.alt)}">
      </div>`).join('');
  const dots = active.pages.map((page, i) => `
        <button type="button" class="dp-beispiele__dot" id="${uid}-tab-${i}" role="tab" data-go="${i}" aria-controls="${uid}-panel-${i}" aria-label="${escapeHtml(`${PAGE_LABELS[page.key]}, Seite ${i + 1} von ${active.pages.length}`)}" aria-selected="${i === 0 ? 'true' : 'false'}" tabindex="${i === 0 ? '0' : '-1'}"></button>`).join('');
  const chips = designs.map((d) => `
        <button type="button" class="dp-beispiele__chip" data-design="${escapeHtml(d.id)}" aria-pressed="${d.id === active.id ? 'true' : 'false'}">${escapeHtml(d.label)}</button>`).join('');
  return `
    <div class="dp-beispiele__book" data-book>
    <div class="dp-beispiele__stage">
      <div class="dp-beispiele__sheet" style="--dp-beispiele-ratio: ${active.width} / ${active.height};">
        <span class="dp-beispiele__wire" aria-hidden="true"></span>
        <div class="dp-beispiele__slides" aria-live="off" data-slides>${slides}
        </div>
      </div>
      <button type="button" class="dp-beispiele__arrow dp-beispiele__arrow--prev" data-nav="prev" aria-label="Vorherige Seite">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button type="button" class="dp-beispiele__arrow dp-beispiele__arrow--next" data-nav="next" aria-label="Nächste Seite">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
    <div class="dp-beispiele__dots" role="tablist" aria-label="Seiten des Beispielkalenders" data-dots>${dots}
    </div>
    <p class="dp-beispiele__caption" id="${uid}-caption" data-caption>${escapeHtml(captionFor(active, active.pages[0].key))}</p>
    </div>
    <div class="dp-beispiele__chips" role="group" aria-label="Design wählen" tabindex="0" data-chips>${chips}
    </div>
    ${active.href ? `<a class="dp-beispiele__cta" href="${escapeHtml(active.href)}" data-cta>${escapeHtml(ctaLabel)}<svg class="dp-beispiele__cta-arrow" viewBox="0 0 20 12" aria-hidden="true" focusable="false"><path d="M0 6h17M12.5 1L18 6l-5.5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a>` : ''}
  `;
}

/* ------------------------------------------------------------------------------------------------ */

// Ohne DOM (Tests unter Node) bleibt die Render-Funktion nutzbar; das Element wird dann nicht registriert.
const BaseElement = typeof HTMLElement === 'function' ? HTMLElement : class {};

class DpBeispiele extends BaseElement {
  /** @type {BeispielDesign[]} */ #designs = [];
  /** @type {BeispielDesign|null} */ #active = null;
  #index = 0;
  #timer = 0;
  #visible = true;
  #hovered = false;
  #focused = false;
  #reducedMotion = false;
  #touchStartX = 0;
  #observer = null;

  connectedCallback() {
    const script = this.querySelector('script[data-dp-beispiele]');
    let raw = null;
    try { raw = script ? JSON.parse(script.textContent || 'null') : null; } catch { raw = null; }
    this.#designs = normalizeDesigns(raw);
    const host = this.querySelector('[data-dp-beispiele-host]');
    if (!host || !this.#designs.length) { this.hidden = true; return; }
    const wanted = String(this.dataset.activeDesign ?? '').toLowerCase();
    this.#active = this.#designs.find((d) => d.id === wanted) ?? this.#designs[0];
    host.innerHTML = renderBeispiele({ designs: this.#designs, activeId: this.#active.id, uid: this.id || 'dp-beispiele', ctaLabel: this.dataset.ctaLabel });
    this.#reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#bind();
    this.#observe();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
  }

  #bind() {
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target).closest('[data-nav], [data-go], [data-design]');
      if (!target || !this.contains(target)) return;
      if (target.dataset.nav) this.#go(this.#index + (target.dataset.nav === 'next' ? 1 : -1), true);
      else if (target.dataset.go !== undefined) this.#go(Number(target.dataset.go), true);
      else if (target.dataset.design) this.#selectDesign(target.dataset.design);
    });
    this.addEventListener('keydown', (event) => {
      const inChips = /** @type {HTMLElement} */ (event.target).closest('[data-chips]');
      if (inChips) return; // Chips: Tab/Pfeile wie normale Buttons, die Scroll-Region rollt selbst
      if (event.key === 'ArrowRight') { this.#go(this.#index + 1, true); event.preventDefault(); }
      else if (event.key === 'ArrowLeft') { this.#go(this.#index - 1, true); event.preventDefault(); }
      else if (event.key === 'Home') { this.#go(0, true); event.preventDefault(); }
      else if (event.key === 'End') { this.#go(this.#pages().length - 1, true); event.preventDefault(); }
    });
    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#stop(); });
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#schedule(); });
    this.addEventListener('focusin', () => { this.#focused = true; this.#stop(); });
    this.addEventListener('focusout', (event) => {
      if (this.contains(/** @type {Node|null} */ (event.relatedTarget))) return;
      this.#focused = false; this.#schedule();
    });
    this.#bindStage();
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.#stop(); else this.#schedule(); });
  }

  /** Wischen auf dem Blatt (die Bühne wird beim Designwechsel ersetzt, deshalb eigene Methode). */
  #bindStage() {
    const stage = this.querySelector('.dp-beispiele__stage');
    if (!stage) return;
    stage.addEventListener('touchstart', (event) => { this.#touchStartX = event.touches[0]?.clientX ?? 0; }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      const dx = (event.changedTouches[0]?.clientX ?? 0) - this.#touchStartX;
      if (Math.abs(dx) >= SWIPE_MIN_PX) this.#go(this.#index + (dx < 0 ? 1 : -1), true);
    }, { passive: true });
  }

  #observe() {
    if (typeof IntersectionObserver !== 'function') return;
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((e) => e.isIntersecting);
      if (this.#visible) this.#schedule(); else this.#stop();
    }, { threshold: 0.35 });
    this.#observer.observe(this);
  }

  #pages() { return this.#active?.pages ?? []; }

  /** @param {number} index @param {boolean} userAction  Nutzeraktion setzt den Auto-Takt neu */
  #go(index, userAction) {
    const pages = this.#pages();
    if (!pages.length) return;
    const next = ((index % pages.length) + pages.length) % pages.length;
    this.#index = next;
    const slides = this.querySelectorAll('.dp-beispiele__slide');
    slides.forEach((slide, i) => { slide.hidden = i !== next; });
    this.querySelectorAll('.dp-beispiele__dot').forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === next ? 'true' : 'false');
      dot.setAttribute('tabindex', i === next ? '0' : '-1');
    });
    const caption = this.querySelector('[data-caption]');
    if (caption && this.#active) caption.textContent = captionFor(this.#active, pages[next].key);
    if (userAction) {
      // Pfeiltaste auf einem Punkt: Fokus wandert mit (Tabs-Muster); Blättern per Pfeil-Button lässt ihn dort.
      const dotFocused = document.activeElement?.classList?.contains('dp-beispiele__dot');
      if (dotFocused) this.querySelectorAll('.dp-beispiele__dot')[next]?.focus();
      this.#schedule();
    }
  }

  /** @param {string} id */
  #selectDesign(id) {
    const design = this.#designs.find((d) => d.id === id);
    if (!design || design === this.#active) return;
    this.#active = design;
    this.#index = 0;
    const host = this.querySelector('[data-dp-beispiele-host]');
    if (!host) return;
    // Sichtbares Blatt neu aufbauen, Chips nur umschalten (Fokus bleibt auf dem gedrückten Chip).
    const fresh = document.createElement('div');
    fresh.innerHTML = renderBeispiele({ designs: this.#designs, activeId: id, uid: this.id || 'dp-beispiele', ctaLabel: this.dataset.ctaLabel });
    const swap = (selector) => {
      const oldEl = host.querySelector(selector);
      const newEl = fresh.querySelector(selector);
      if (oldEl && newEl) oldEl.replaceWith(newEl);
      else if (oldEl && !newEl) oldEl.remove();
      else if (!oldEl && newEl) host.appendChild(newEl);
    };
    swap('[data-book]');
    swap('[data-cta]');
    this.querySelectorAll('.dp-beispiele__chip').forEach((chip) => {
      chip.setAttribute('aria-pressed', chip.getAttribute('data-design') === id ? 'true' : 'false');
    });
    this.#bindStage();
    this.#schedule();
  }

  #schedule() {
    this.#stop();
    if (this.#reducedMotion || this.#hovered || this.#focused || !this.#visible || document.hidden || this.#pages().length < 2) return;
    this.#timer = window.setTimeout(() => { this.#go(this.#index + 1, false); this.#schedule(); }, AUTOPLAY_MS);
    this.#live('off'); // beim Auto-Blättern keine Ansagen (APG Karussell)
  }

  #stop() {
    if (this.#timer) { clearTimeout(this.#timer); this.#timer = 0; }
    this.#live('polite'); // pausiert: die gewählte Seite wird angesagt
  }

  /** @param {'off'|'polite'} value */
  #live(value) {
    this.querySelector('[data-slides]')?.setAttribute('aria-live', value);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('dp-beispiele')) customElements.define('dp-beispiele', DpBeispiele);
